// ============================================================
// Phexotial — Toolbar Popup (Signal Console)
// ============================================================
import { useState, useEffect, useCallback, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import '../assets/fonts/oxanium.css';
import {
  Telescope, Crosshair, Settings, HelpCircle,
  CheckCircle2, AlertCircle, Info, Loader2, Download,
} from 'lucide-react';
import { Dropdown } from './Dropdown';
import {
  capturePage,
  isCapturableUrl,
  isExtensionPageUrl,
  type CaptureResult,
  type CaptureDestination,
} from './capture';
import {
  getExtensionPrefs,
  setExtensionPrefs,
  getDiagnostics,
  type ExtensionPrefs,
} from './prefs';
import {
  type ObservatoryStatus,
  type ExtensionMessage,
} from './messaging';
import { exportAllData } from '@/storage/db';
import { localDateKey } from '@/utils/datetime';

function Popup() {
  const [prefs, setPrefs] = useState<ExtensionPrefs | null>(null);
  const [status, setStatus] = useState<ObservatoryStatus | null>(null);
  const [captureResult, setCaptureResult] = useState<CaptureResult | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<'success' | 'error' | null>(null);
  const captureLockRef = useRef(false);

  useEffect(() => {
    void loadPrefs();
    void loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPrefs = useCallback(async () => {
    const p = await getExtensionPrefs();
    setPrefs(p);
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const response = await chrome.runtime?.sendMessage?.({ type: 'get-observatory-status' });
      if (response && typeof response === 'object') {
        const r = response as { type: string; payload?: ObservatoryStatus };
        if (r.type === 'observatory-status' && r.payload) {
          setStatus(r.payload);
          return;
        }
      }
    } catch {
      // Fallback: get diagnostics directly
    }
    const diag = await getDiagnostics();
    setStatus({
      version: diag.version,
      dataAvailable: false,
      lastRefresh: diag.lastUpdate,
      degradedCount: 0,
      online: navigator.onLine,
    });
  }, []);

  const handleCapture = useCallback(async () => {
    if (captureLockRef.current || capturing) return;
    captureLockRef.current = true;
    setCapturing(true);
    setError(null);
    setCaptureResult(null);

    try {
      const tabs = await chrome.tabs?.query?.({ active: true, currentWindow: true });
      const activeTab = tabs?.[0];
      if (!activeTab?.url) {
        setError('No active tab found');
        return;
      }

      const urlCheck = isCapturableUrl(activeTab.url);
      if (!urlCheck.capturable) {
        setCaptureResult({ status: 'unsupported-url', reason: urlCheck.reason ?? 'Unsupported URL' });
        return;
      }

      if (isExtensionPageUrl(activeTab.url)) {
        setCaptureResult({ status: 'unsupported-url', reason: 'Cannot capture Phexotial extension pages' });
        return;
      }

      const request = {
        url: activeTab.url,
        title: activeTab.title ?? activeTab.url,
        faviconUrl: activeTab.favIconUrl,
        source: 'extension-capture',
      };

      const result = await capturePage(request, prefs?.defaultCaptureDestination ?? 'reading-queue');
      setCaptureResult(result);

      if (result.status === 'created' && prefs?.closePopupOnCapture) {
        window.close();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Capture failed';
      setError(message);
    } finally {
      setCapturing(false);
      captureLockRef.current = false;
    }
  }, [capturing, prefs]);

  const handleOpenObservatory = useCallback(async () => {
    const msg: ExtensionMessage = { type: 'open-observatory' };
    try {
      await chrome.runtime?.sendMessage?.(msg);
    } catch {
      // Fallback: open directly
      await chrome.tabs?.create?.({ url: 'index.html' });
    }
    window.close();
  }, []);

  const handleOpenSettings = useCallback(async () => {
    const msg: ExtensionMessage = { type: 'open-options' };
    try {
      await chrome.runtime?.sendMessage?.(msg);
    } catch {
      await chrome.tabs?.create?.({ url: 'options.html' });
    }
    window.close();
  }, []);

  const handleBackup = useCallback(async () => {
    setExporting(true);
    setExportResult(null);
    setError(null);
    try {
      const data = await exportAllData();
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `phexotial-backup-${localDateKey()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setExportResult('success');
      setTimeout(() => window.close(), 1200);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Export failed';
      setError(msg);
      setExportResult('error');
      // Fallback: open the site with hash routing to highlight export button
      await chrome.tabs?.create?.({ url: 'index.html#data-export' });
      window.close();
    } finally {
      setExporting(false);
    }
  }, []);

  const handleOpenHelp = useCallback(async () => {
    await chrome.tabs?.create?.({ url: 'welcome.html' });
    window.close();
  }, []);

  const handleDestinationChange = useCallback(async (dest: CaptureDestination) => {
    await setExtensionPrefs({ defaultCaptureDestination: dest });
    setPrefs((prev) => prev ? { ...prev, defaultCaptureDestination: dest } : prev);
  }, []);

  const version = status?.version ?? '1.2.0';

  return (
    <div className="phex-popup" style={{ width: '340px', minHeight: '200px' }}>
      {/* Header */}
      <div className="phex-popup-header">
        <div className="phex-popup-brand" style={{ position: 'relative' }}>
          <Telescope size={16} aria-hidden="true" />
          <span className="phex-popup-wordmark">PHEXO//TIAL</span>
          <span aria-hidden="true" style={{
            position: 'absolute',
            top: -2,
            right: -6,
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: '#ffd76a',
            border: '1.5px solid #0a0e1a',
            boxShadow: '0 0 4px rgba(255, 215, 106, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#0a0e1a' }} />
          </span>
        </div>
        <span className="phex-popup-version">v{version}</span>
      </div>

      {/* Status */}
      <div className="phex-popup-status" role="status" aria-live="polite">
        <div className="phex-popup-status-row">
          <span className={`phex-popup-dot ${status?.online ? 'online' : 'offline'}`} aria-hidden="true" />
          <span className="phex-popup-status-text">
            {status?.online ? 'Online' : 'Offline'}
            {status && status.degradedCount > 0 && ` · ${status.degradedCount} degraded`}
          </span>
        </div>
        {status?.lastRefresh && (
          <div className="phex-popup-status-sub">Last update: {status.lastRefresh}</div>
        )}
      </div>

      {/* Capture result */}
      {captureResult && (
        <div
          className={`phex-popup-result ${
            captureResult.status === 'created' ? 'success' :
            captureResult.status === 'duplicate' ? 'info' :
            captureResult.status === 'unsupported-url' ? 'warning' : 'error'
          }`}
          role="alert"
          aria-live="assertive"
        >
          {captureResult.status === 'created' && (
            <><CheckCircle2 size={14} aria-hidden="true" /> <span>Saved to {captureResult.destination === 'reading-queue' ? 'Reading Queue' : 'Bookmarks'}</span></>
          )}
          {captureResult.status === 'duplicate' && (
            <><Info size={14} aria-hidden="true" /> <span>Already in {captureResult.destination === 'reading-queue' ? 'Reading Queue' : 'Bookmarks'}</span></>
          )}
          {captureResult.status === 'unsupported-url' && (
            <><AlertCircle size={14} aria-hidden="true" /> <span>{captureResult.reason}</span></>
          )}
          {captureResult.status === 'failed' && (
            <><AlertCircle size={14} aria-hidden="true" /> <span>{captureResult.reason}</span></>
          )}
        </div>
      )}

      {error && (
        <div className="phex-popup-result error" role="alert">
          <AlertCircle size={14} aria-hidden="true" /> <span>{error}</span>
        </div>
      )}

      {/* Actions */}
      <div className="phex-popup-actions">
        <button
          className="phex-popup-btn primary"
          onClick={handleCapture}
          disabled={capturing}
          aria-label="Capture current page"
        >
          {capturing ? <Loader2 size={14} className="phex-spin" aria-hidden="true" /> : <Crosshair size={14} aria-hidden="true" />}
          <span>{capturing ? 'Capturing…' : 'Capture Current Page'}</span>
        </button>

        <button
          className="phex-popup-btn"
          onClick={handleOpenObservatory}
          aria-label="Open Observatory"
        >
          <Telescope size={14} aria-hidden="true" />
          <span>Open Observatory</span>
        </button>
      </div>

      {/* Destination selector */}
      <div className="phex-popup-destination">
        <label className="phex-popup-label">Default destination</label>
        <Dropdown
          value={prefs?.defaultCaptureDestination ?? 'reading-queue'}
          options={[
            { value: 'reading-queue', label: 'Reading Queue' },
            { value: 'bookmarks', label: 'Bookmarks' },
          ]}
          onChange={(v) => void handleDestinationChange(v as CaptureDestination)}
          className="phex-popup-dropdown"
          ariaLabel="Default capture destination"
        />
      </div>

      {/* Footer links */}
      <div className="phex-popup-footer">
        <button className="phex-popup-link" onClick={handleOpenSettings} aria-label="Open settings">
          <Settings size={12} aria-hidden="true" /> Settings
        </button>
        <button className="phex-popup-link" onClick={handleBackup} disabled={exporting} aria-label="Export backup">
          {exporting
            ? <Loader2 size={12} className="phex-spin" aria-hidden="true" />
            : exportResult === 'success'
              ? <CheckCircle2 size={12} aria-hidden="true" />
              : <Download size={12} aria-hidden="true" />}
          {exporting ? 'Exporting…' : exportResult === 'success' ? 'Saved!' : 'Backup'}
        </button>
        <button className="phex-popup-link" onClick={handleOpenHelp} aria-label="Open help and welcome page">
          <HelpCircle size={12} aria-hidden="true" /> Help
        </button>
      </div>
    </div>
  );
}

// --- Inline styles (popup has no access to Tailwind) ---
const style = document.createElement('style');
style.textContent = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Oxanium', system-ui, sans-serif; background: #0a0e1a; color: #d7dbea; }
  .phex-popup { padding: 12px; }
  .phex-popup-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
  .phex-popup-brand { display: flex; align-items: center; gap: 6px; color: #8bc8ff; font-weight: 600; font-size: 14px; letter-spacing: 0.05em; }
  .phex-popup-wordmark { font-family: 'Oxanium', monospace; }
  .phex-popup-version { font-size: 10px; color: #6b7280; }
  .phex-popup-status { padding: 8px 10px; background: rgba(255,255,255,0.03); border-radius: 8px; margin-bottom: 10px; border: 1px solid rgba(255,255,255,0.06); }
  .phex-popup-status-row { display: flex; align-items: center; gap: 8px; }
  .phex-popup-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .phex-popup-dot.online { background: #22c55e; }
  .phex-popup-dot.offline { background: #ef4444; }
  .phex-popup-status-text { font-size: 12px; color: #b0b8d0; }
  .phex-popup-status-sub { font-size: 10px; color: #6b7280; margin-top: 4px; padding-left: 16px; }
  .phex-popup-result { display: flex; align-items: center; gap: 6px; padding: 8px 10px; border-radius: 8px; margin-bottom: 10px; font-size: 12px; }
  .phex-popup-result.success { background: rgba(34,197,94,0.1); color: #4ade80; border: 1px solid rgba(34,197,94,0.2); }
  .phex-popup-result.info { background: rgba(59,130,246,0.1); color: #60a5fa; border: 1px solid rgba(59,130,246,0.2); }
  .phex-popup-result.warning { background: rgba(245,158,11,0.1); color: #fbbf24; border: 1px solid rgba(245,158,11,0.2); }
  .phex-popup-result.error { background: rgba(239,68,68,0.1); color: #f87171; border: 1px solid rgba(239,68,68,0.2); }
  .phex-popup-actions { display: flex; flex-direction: column; gap: 6px; margin-bottom: 10px; }
  .phex-popup-btn { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.04); color: #d7dbea; font-size: 12px; font-family: inherit; cursor: pointer; transition: background 0.15s; min-height: 36px; }
  .phex-popup-btn:hover:not(:disabled) { background: rgba(255,255,255,0.08); }
  .phex-popup-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .phex-popup-btn.primary { background: rgba(139,200,255,0.15); border-color: rgba(139,200,255,0.3); color: #8bc8ff; }
  .phex-popup-btn.primary:hover:not(:disabled) { background: rgba(139,200,255,0.25); }
  .phex-popup-btn:focus-visible { outline: 2px solid #8bc8ff; outline-offset: 1px; }
  .phex-popup-destination { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
  .phex-popup-label { font-size: 11px; color: #6b7280; white-space: nowrap; }
  .phex-popup-dropdown { flex: 1; }
  .phex-dropdown { position: relative; }
  .phex-dropdown-trigger { display: flex; align-items: center; justify-content: space-between; gap: 6px; width: 100%; padding: 7px 10px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.08); background: rgba(255,255,255,0.04); color: #d7dbea; font-size: 12px; font-family: inherit; cursor: pointer; transition: border-color 0.15s, background 0.15s; }
  .phex-dropdown-trigger:hover { background: rgba(255,255,255,0.07); border-color: rgba(255,255,255,0.12); }
  .phex-dropdown-trigger:focus-visible { outline: 2px solid #8bc8ff; outline-offset: 1px; }
  .phex-dropdown-value { flex: 1; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .phex-dropdown-chevron { flex-shrink: 0; color: #6b7280; }
  .phex-dropdown-menu { position: absolute; bottom: calc(100% + 4px); left: 0; right: 0; z-index: 100; background: #111627; border: 1px solid rgba(139,200,255,0.15); border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,0,0,0.2); padding: 4px; display: flex; flex-direction: column; gap: 2px; }
  .phex-dropdown-item { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 6px; border: none; background: none; color: #b0b8d0; font-size: 12px; font-family: inherit; cursor: pointer; transition: background 0.12s, color 0.12s; text-align: left; width: 100%; position: relative; }
  .phex-dropdown-item.focused { background: rgba(139,200,255,0.1); color: #d7dbea; }
  .phex-dropdown-item.selected { color: #8bc8ff; }
  .phex-dropdown-item.selected.focused { background: rgba(139,200,255,0.15); }
  .phex-dropdown-item:hover { background: rgba(139,200,255,0.1); color: #d7dbea; }
  .phex-dropdown-item-label { flex: 1; }
  .phex-dropdown-item-desc { font-size: 10px; color: #6b7280; }
  .phex-dropdown-check { flex-shrink: 0; color: #8bc8ff; }
  .phex-popup-footer { display: flex; gap: 4px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.06); }
  .phex-popup-link { display: flex; align-items: center; gap: 4px; padding: 6px 8px; border: none; background: none; color: #6b7280; font-size: 11px; font-family: inherit; cursor: pointer; border-radius: 6px; transition: color 0.15s, background 0.15s; }
  .phex-popup-link:hover { color: #b0b8d0; background: rgba(255,255,255,0.04); }
  .phex-popup-link:focus-visible { outline: 2px solid #8bc8ff; outline-offset: 1px; }
  .phex-spin { animation: phex-spin 0.8s linear infinite; }
  @keyframes phex-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) { .phex-spin { animation: none; } }
`;
document.head.appendChild(style);

createRoot(document.getElementById('root')!).render(<Popup />);
